const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message || code);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** True when a folder DELETE needs a force retry (non-empty). */
export function isFolderNotEmptyError(err: unknown): boolean {
  if (isApiError(err)) {
    return err.code === "folder_not_empty" || err.status === 409;
  }
  const msg = String((err as Error)?.message || err);
  return msg.includes("folder_not_empty") || msg.includes("not empty") || msg.includes("Pass force=1");
}

/** Absolute API path (uses VITE_API_URL when set; otherwise same-origin / proxy). */
export function apiUrl(path: string): string {
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

/** WebSocket URL for live feeds (mirrors apiUrl with wss/ws protocol). */
export function wsUrl(path: string): string {
  const base = API_BASE || (typeof window !== "undefined" ? window.location.origin : "");
  const url = new URL(path.startsWith("/") ? path : `/${path}`, base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export async function api<T>(
  path: string,
  opts: RequestInit & { admin?: boolean; sessionId?: string } = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body && !(opts.body instanceof FormData) && !(opts.body instanceof Blob)) {
    headers.set("Content-Type", "application/json");
  }
  const isAdminCall = opts.admin !== false && !path.includes("/viewer/") && !path.startsWith("/api/v1/auth/login") && !path.startsWith("/api/v1/auth/setup") && !path.startsWith("/api/v1/auth/status");
  if (isAdminCall) {
    const token = localStorage.getItem("adminSessionToken");
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  if (opts.sessionId) headers.set("X-Viewer-Session", opts.sessionId);
  const fp = localStorage.getItem("deviceFp");
  if (fp) headers.set("X-Device-Fingerprint", fp);

  const res = await fetch(apiUrl(path), { ...opts, headers });
  const ct = res.headers.get("content-type") || "";

  if (!res.ok) {
    if (ct.includes("application/json")) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      const code = err.error || `request_failed_${res.status}`;
      const message = err.message || err.error || `request_failed_${res.status}`;
      throw new ApiError(code, message, res.status);
    }
    const fallback =
      res.status === 503
        ? "API not configured. Deploy the API Worker and set VITE_API_URL."
        : `request_failed_${res.status}`;
    throw new ApiError(`request_failed_${res.status}`, fallback, res.status);
  }

  if (!ct.includes("application/json")) {
    // SPA HTML mistaken for API success used to crash the UI (blank page).
    throw new Error("API returned non-JSON. Set VITE_API_URL to your API Worker URL and redeploy.");
  }

  return res.json() as Promise<T>;
}

export function ensureDeviceFingerprint(): string {
  let fp = localStorage.getItem("deviceFp");
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem("deviceFp", fp);
  }
  return fp;
}

export function applyTenantFromHost(): "spielmann" | "blackcoffee" {
  const host = window.location.host.toLowerCase();
  const slug = host.includes("blackcoffee") ? "blackcoffee" : "spielmann";
  document.documentElement.dataset.tenant = slug;
  return slug;
}

export function apiBaseConfigured(): boolean {
  return Boolean(API_BASE);
}
